import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
    Component,
    ElementRef,
    HostListener,
    Injector,
    ViewChild,
    afterNextRender,
    inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import Joi from 'joi';
import { lastValueFrom } from 'rxjs';
import { HttpNewService } from 'src/app/services/application/http-new.service';
import { LoadService } from 'src/app/services/application/load.service';
import { PatientService } from 'src/app/services/patient.service';
import { AlertService } from 'src/lib/ariadna/alert';

interface IFeedbackOption {
    title: string;
    email: string[];
    subthemes?: string[]; // Fixed typo: was "subsubject", should be "subthemes"
}

interface FeedbackDTO {
    subject: string;
    mail_from: string;
    mail_to: string;
    content: string;
    subsubject?: string;
}

@Component({
    selector: 'app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
})
export class FeedbackComponent {
    form: FormGroup = new FormGroup({
        subject: new FormControl('', Validators.required),
        subsubject: new FormControl(''),
        message: new FormControl('', Validators.required),
        email: new FormControl('', [Validators.email, Validators.required]),
    });
    subjectOptions: IFeedbackOption[] = [];
    loading = false;
    protected patientEmail: string | null = null;
    private _injector = inject(Injector);
    isSubjectOpen = false;
    isSubsubjectOpen = false;
    selectedSubject: any = null;
    selectedSubsubject: string | null = null;

    // For confirmation dialog
    showConfirmation = false;

    // Store initial form values to compare later
    private initialFormValues: any;

    @ViewChild('subjectDropdown') subjectDropdown!: ElementRef;
    @ViewChild('subsubjectDropdown') subsubjectDropdown!: ElementRef;
    @ViewChild('autosize') autosize!: CdkTextareaAutosize;

    triggerResize() {
        // Wait for content to render, then trigger textarea resize.
        afterNextRender(
            () => {
                this.autosize.resizeToFitContent(true);
            },
            {
                injector: this._injector,
            }
        );
    }

    constructor(
        private load: LoadService,
        private http: HttpNewService,
        private alertService: AlertService,
        private patientService: PatientService,
        private el: ElementRef,
        private dialogRef: MatDialogRef<FeedbackComponent>
    ) {
        if (load.configData.pages.feedback.length === 0) {
            alertService.error('Нет полей для формы обратной связи!');
            throw new Error('Нет полей для формы обратной связи!');
        }

        // Disable default closing behavior
        this.dialogRef.disableClose = true;

        // Subscribe to backdrop clicks
        this.dialogRef.backdropClick().subscribe(() => {
            this.attemptClose();
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.attemptClose();
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        // Only check if the subject dropdown exists
        if (
            this.subjectDropdown &&
            !this.subjectDropdown.nativeElement.contains(target)
        ) {
            this.isSubjectOpen = false;
        }

        // Only check if the subsubject dropdown exists
        if (
            this.subsubjectDropdown &&
            !this.subsubjectDropdown.nativeElement.contains(target)
        ) {
            this.isSubsubjectOpen = false;
        }
    }

    async ngOnInit() {
        try {
            const rawJson = this.load.configData.pages.feedback.subjects;
            const parsed = JSON.parse(rawJson);

            // Normalize email: ensure it's always an array
            this.subjectOptions = parsed['subjects'].map((subj: any) => ({
                title: subj.title,
                email: Array.isArray(subj.email) ? subj.email : [subj.email],
                subthemes: Array.isArray(subj.subthemes) ? subj.subthemes : [],
            }));

            const patientInfo = await lastValueFrom(
                this.patientService.getServerPatientInfo$()
            );
            this.patientEmail = patientInfo.email ?? null;

            this.form.patchValue({
                email: this.patientEmail || '',
                message: '',
            });

            this.initialFormValues = {
                email: this.patientEmail || '',
                message: '',
                subject: null,
                subsubject: '',
            };
        } catch (error) {
            console.error('Ошибка загрузки/парсинга данных формы:', error);
            this.alertService.error(
                'Не удалось загрузить форму обратной связи'
            );
        }

        // Email validator
        const emailControl = this.form.get('email');
        emailControl?.setValidators([
            Validators.required,
            this.joiEmailValidator.bind(this),
        ]);
        emailControl?.updateValueAndValidity();

        // Watch subject changes
        this.form
            .get('subject')
            ?.valueChanges.subscribe((subject: IFeedbackOption) => {
                this.selectedSubject = subject;
                const subControl = this.form.get('subsubject');

                if (subject?.subthemes && subject.subthemes.length > 0) {
                    subControl?.setValidators([Validators.required]);
                } else {
                    subControl?.clearValidators();
                }
                subControl?.updateValueAndValidity();
            });
    }

    toggleSubjectDropdown() {
        this.isSubjectOpen = !this.isSubjectOpen;
        this.isSubsubjectOpen = false; // Close other dropdown
    }

    toggleSubsubjectDropdown() {
        this.isSubsubjectOpen = !this.isSubsubjectOpen;
        this.isSubjectOpen = false; // Close other dropdown
    }

    selectSubject(subject: any, event: Event) {
        event.stopPropagation(); // Prevent closure on selection
        this.form.get('subject')?.setValue(subject);
        this.selectedSubject = subject;

        // Reset subsubject selection when changing subject
        this.selectedSubsubject = null;
        this.form.get('subsubject')?.setValue(null);

        this.isSubjectOpen = false;
    }

    selectSubsubject(subsubject: string, event: Event) {
        event.stopPropagation(); // Prevent closure on selection
        this.form.get('subsubject')?.setValue(subsubject);
        this.selectedSubsubject = subsubject;
        this.isSubsubjectOpen = false;
    }

    // Check if form has been modified
    private isFormModified(): boolean {
        if (!this.initialFormValues) return false;

        const currentValues = this.form.value;
        return (
            currentValues.message !== this.initialFormValues.message ||
            currentValues.email !== this.initialFormValues.email ||
            currentValues.subject !== this.initialFormValues.subject ||
            currentValues.subsubject !== this.initialFormValues.subsubject
        );
    }

    // New methods for handling close confirmation
    attemptClose(): void {
        // Check if form has any content
        if (this.isFormModified()) {
            this.showConfirmation = true;
        } else {
            this.dialogRef.close();
        }
    }

    cancelClose(): void {
        this.showConfirmation = false;
    }

    confirmClose(): void {
        this.dialogRef.close();
    }

    async onSubmit() {
        this.loading = true;
        this.dialogRef.disableClose = false;
        if (!this.form.valid) {
            this.alertService.error('Заполните все обязательные поля!');
            this.loading = false;
            return;
        }

        const data: FeedbackDTO = {
            subject: this.form.controls['subject'].value.title,
            mail_from: this.form.controls['email'].value,
            mail_to: this.form.controls['subject'].value.email.join(','),
            content: this.form.controls['message'].value,
        };

        if (this.form.controls['subsubject'].value) {
            data['subsubject'] = this.form.controls['subsubject'].value;
        }

        try {
            await lastValueFrom(this.http.post('/api/feedback', data, false));
            this.alertService.success('Ваше обращение успешно отправлено!');
            this.dialogRef.close();
        } catch (error: any) {
            this.alertService.error('Не удалось отправить обращение!');
            this.dialogRef.disableClose = false; // Теперь можно закрыть
            console.error('Ошибка отправки:', error?.msg || error);
        } finally {
            this.loading = false;
        }
    }

    private emailSchema = Joi.string().email({ tlds: { allow: false } });

    joiEmailValidator(control: FormControl): { [key: string]: any } | null {
        const value = control.value?.trim();
        if (!value) return null;
        const isValid = this.emailSchema.validate(value).error === undefined;
        return isValid ? null : { invalidEmail: true };
    }
}
